import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { Amplify } from "aws-amplify";
import { 
  signIn, 
  signOut, 
  resetPassword,
  confirmResetPassword,
  fetchAuthSession, 
  getCurrentUser 
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import CONFIG from "../config";

// Configure Amplify
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: CONFIG.cognito.userPoolId,
      userPoolClientId: CONFIG.cognito.clientId,
      signUpVerificationMethod: "code",
      loginWith: { email: true },
    },
  },
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [authMethod, setAuthMethod] = useState(null); // "midway" | "cognito" | null
  const [isMidwayLoading, setIsMidwayLoading] = useState(false);

  const isWriteUser = authMethod === "midway";

  // --- Refs for token refresh ---
  const refreshTimerRef = useRef(null);
  const refreshInProgressRef = useRef(null);

  // --- Midway token storage helpers ---

  const storeMidwayTokens = (tokenResponse) => {
    const expiresAt = Date.now() + tokenResponse.expires_in * 1000;
    sessionStorage.setItem("nova_sonic_auth_method", "midway");
    sessionStorage.setItem("nova_sonic_auth_access_token", tokenResponse.access_token);
    sessionStorage.setItem("nova_sonic_auth_id_token", tokenResponse.id_token);
    sessionStorage.setItem("nova_sonic_auth_refresh_token", tokenResponse.refresh_token);
    sessionStorage.setItem("nova_sonic_auth_expires_at", String(expiresAt));
  };

  const clearMidwayTokens = () => {
    sessionStorage.removeItem("nova_sonic_auth_method");
    sessionStorage.removeItem("nova_sonic_auth_access_token");
    sessionStorage.removeItem("nova_sonic_auth_id_token");
    sessionStorage.removeItem("nova_sonic_auth_refresh_token");
    sessionStorage.removeItem("nova_sonic_auth_expires_at");
  };

  const getMidwayTokens = () => {
    return {
      method: sessionStorage.getItem("nova_sonic_auth_method"),
      accessToken: sessionStorage.getItem("nova_sonic_auth_access_token"),
      idToken: sessionStorage.getItem("nova_sonic_auth_id_token"),
      refreshToken: sessionStorage.getItem("nova_sonic_auth_refresh_token"),
      expiresAt: sessionStorage.getItem("nova_sonic_auth_expires_at"),
    };
  };

  const parseIdToken = (idToken) => {
    try {
      const payload = idToken.split(".")[1];
      const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
      const claims = JSON.parse(decoded);
      return {
        sub: claims.sub || "",
        preferred_username: claims.preferred_username || "",
        email: claims.email || "",
        given_name: claims.given_name || "",
        family_name: claims.family_name || "",
      };
    } catch {
      return { sub: "", preferred_username: "", email: "", given_name: "", family_name: "" };
    }
  };

  const refreshMidwayTokens = async () => {
    // Deduplicate concurrent refresh calls
    if (refreshInProgressRef.current) {
      return refreshInProgressRef.current;
    }

    const refreshPromise = (async () => {
      try {
        const { refreshToken } = getMidwayTokens();
        if (!refreshToken) {
          clearMidwayTokens();
          setIsAuthenticated(false);
          setAuthMethod(null);
          setUser(null);
          return false;
        }

        const tokenUrl = `${CONFIG.cognito.domain}/oauth2/token`;
        const body = new URLSearchParams({
          grant_type: "refresh_token",
          client_id: CONFIG.cognito.clientId,
          refresh_token: refreshToken,
        });

        const response = await fetch(tokenUrl, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        });

        if (!response.ok) {
          clearMidwayTokens();
          setIsAuthenticated(false);
          setAuthMethod(null);
          setUser(null);
          return false;
        }

        const tokens = await response.json();

        // Refresh response may not include refresh_token — keep existing one
        const mergedTokens = {
          ...tokens,
          refresh_token: tokens.refresh_token || refreshToken,
        };

        storeMidwayTokens(mergedTokens);

        const claims = parseIdToken(mergedTokens.id_token);
        setUser({
          email: claims.email,
          sub: claims.sub,
          preferred_username: claims.preferred_username,
          given_name: claims.given_name,
          family_name: claims.family_name,
          groups: [],
          authMethod: "midway",
        });

        scheduleTokenRefresh(tokens.expires_in);

        return true;
      } catch {
        clearMidwayTokens();
        setIsAuthenticated(false);
        setAuthMethod(null);
        setUser(null);
        return false;
      } finally {
        refreshInProgressRef.current = null;
      }
    })();

    refreshInProgressRef.current = refreshPromise;
    return refreshPromise;
  };

  const scheduleTokenRefresh = (expiresInSeconds) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const refreshBuffer = 2 * 60; // 2 minutes before expiry
    const refreshIn = Math.max((expiresInSeconds - refreshBuffer) * 1000, 0);
    refreshTimerRef.current = setTimeout(() => refreshMidwayTokens(), refreshIn);
  };

  const checkAuth = useCallback(async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);

      // 1. Check URL for ?code= parameter (Midway OAuth callback)
      const code = urlParams.get("code");
      if (code) {
        // Clean the URL immediately to prevent re-processing
        window.history.replaceState({}, document.title, window.location.pathname);
        await exchangeCodeForTokens(code);
        setIsLoading(false);
        return;
      }

      // 2. Check URL for ?error= parameter (Midway OAuth error callback)
      const oauthError = urlParams.get("error");
      if (oauthError) {
        const errorDescription = urlParams.get("error_description") || oauthError;
        setError(errorDescription);
        window.history.replaceState({}, document.title, window.location.pathname);
        setIsLoading(false);
        return;
      }

      // 3. Check sessionStorage for existing Midway tokens
      const storedMethod = sessionStorage.getItem("nova_sonic_auth_method");
      if (storedMethod === "midway") {
        const { accessToken, idToken, expiresAt } = getMidwayTokens();
        if (accessToken && idToken) {
          const claims = parseIdToken(idToken);
          setUser({
            email: claims.email,
            sub: claims.sub,
            preferred_username: claims.preferred_username,
            given_name: claims.given_name,
            family_name: claims.family_name,
            groups: [],
            authMethod: "midway",
          });
          setAuthMethod("midway");
          setIsAuthenticated(true);

          // Schedule token refresh based on remaining time
          if (expiresAt) {
            const remainingMs = Number(expiresAt) - Date.now();
            const remainingSeconds = Math.max(Math.floor(remainingMs / 1000), 0);
            scheduleTokenRefresh(remainingSeconds);
          }

          setIsLoading(false);
          return;
        }
        // Tokens missing/invalid — clear stale session
        clearMidwayTokens();
      }

      // 4. Fall through to existing Amplify session check
      const session = await fetchAuthSession();
      if (session.tokens) {
        const currentUser = await getCurrentUser();
        const groups = (session.tokens.accessToken.payload["cognito:groups"]) || [];
        const email = currentUser.signInDetails?.loginId || session.tokens.idToken?.payload?.email || "";
        const sub = session.tokens.idToken?.payload?.sub || "";
        setUser({ email, groups, sub });
        setAuthMethod("cognito");
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch {
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkAuth();
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signedIn") checkAuth();
      else if (payload.event === "signedOut") {
        setIsAuthenticated(false);
        setUser(null);
      }
    });
    return () => {
      unsubscribe();
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [checkAuth]);

  const handleSignIn = async (email, password) => {
    setError(null);
    try {
      const result = await signIn({ username: email, password });
      return { success: true, ...result };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const handleForgotPassword = async (email) => {
    setError(null);
    try {
      await resetPassword({ username: email });
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const handleConfirmResetPassword = async (email, code, newPassword) => {
    setError(null);
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const handleMidwaySignIn = () => {
    const cognitoDomain = CONFIG.cognito.domain;
    const clientId = CONFIG.cognito.clientId;
    // Use current origin dynamically so both custom domain and CloudFront URL work
    const callbackUrl = encodeURIComponent(`${window.location.origin}/auth/callback`);

    setIsMidwayLoading(true);

    window.location.href =
      `${cognitoDomain}/oauth2/authorize?` +
      `response_type=code&` +
      `client_id=${clientId}&` +
      `redirect_uri=${callbackUrl}&` +
      `scope=openid+email+profile&` +
      `identity_provider=AmazonFederate`;
  };

  const exchangeCodeForTokens = async (code) => {
    try {
      const tokenUrl = `${CONFIG.cognito.domain}/oauth2/token`;
      const body = new URLSearchParams({
        grant_type: "authorization_code",
        code,
        client_id: CONFIG.cognito.clientId,
        // Must match the redirect_uri used in the authorize request
        redirect_uri: `${window.location.origin}/auth/callback`,
      });

      const response = await fetch(tokenUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.error_description || errorData.error || "Token exchange failed";
        setError(message);
        setIsMidwayLoading(false);
        window.history.replaceState({}, document.title, window.location.pathname);
        return { success: false, error: message };
      }

      const tokens = await response.json();

      storeMidwayTokens(tokens);

      const claims = parseIdToken(tokens.id_token);

      setUser({
        email: claims.email,
        sub: claims.sub,
        preferred_username: claims.preferred_username,
        given_name: claims.given_name,
        family_name: claims.family_name,
        groups: [],
        authMethod: "midway",
      });

      setAuthMethod("midway");
      setIsAuthenticated(true);
      setIsMidwayLoading(false);

      scheduleTokenRefresh(tokens.expires_in);

      window.history.replaceState({}, document.title, window.location.pathname);

      return { success: true };
    } catch (err) {
      const message = err.message || "Failed to complete sign-in";
      setError(message);
      setIsMidwayLoading(false);
      window.history.replaceState({}, document.title, window.location.pathname);
      return { success: false, error: message };
    }
  };

  const handleSignOut = async () => {
    try {
      if (authMethod === "midway") {
        // Clear refresh timer
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        // Clear all Midway tokens from sessionStorage
        clearMidwayTokens();
        // Reset state
        setIsAuthenticated(false);
        setAuthMethod(null);
        setUser(null);
        // Redirect to Cognito logout endpoint
        const logoutUrl = `${CONFIG.cognito.domain}/logout?client_id=${CONFIG.cognito.clientId}&logout_uri=${encodeURIComponent(window.location.origin)}`;
        window.location.href = logoutUrl;
      } else {
        // Existing Amplify signOut for email/password users
        await signOut();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const getToken = async () => {
    // Midway flow: return id_token from sessionStorage
    if (authMethod === "midway") {
      const { idToken } = getMidwayTokens();
      return idToken || null;
    }
    // Cognito/Amplify flow: return ID token from fetchAuthSession
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  };

  const getUserSub = async () => {
    // Midway flow: return sub from user state
    if (authMethod === "midway") {
      return user?.sub || null;
    }
    // Cognito/Amplify flow: return sub from ID token
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.payload?.sub || null;
    } catch {
      return null;
    }
  };

  const getInitials = () => {
    // Midway users: first two chars of preferred_username uppercased
    if (authMethod === "midway" && user?.preferred_username && user.preferred_username.length >= 2) {
      return user.preferred_username.substring(0, 2).toUpperCase();
    }
    // Email/password users: derive from email local part
    if (!user?.email) return "?";
    const parts = user.email.split("@")[0].split(".");
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].substring(0, 2).toUpperCase();
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      user,
      error,
      isWriteUser,
      authMethod,
      isMidwayLoading,
      midwaySignIn: handleMidwaySignIn,
      refreshMidwayTokens,
      signIn: handleSignIn,
      forgotPassword: handleForgotPassword,
      confirmResetPassword: handleConfirmResetPassword,
      signOut: handleSignOut,
      getToken,
      getUserSub,
      getInitials,
      clearError: () => setError(null),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
